import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  ActivityIndicator,
  TouchableOpacity,
  Alert,
  TextInput,
  Modal,
  Pressable,
  Image,
  Animated,
  Easing,
  useWindowDimensions,
} from 'react-native';
import { generateStatementPdf, STATEMENT_COLUMNS, StatementType, StatementOrder } from '../utills/statementPdf';
import { Picker } from '@react-native-picker/picker';
import { usePendingOrders, updateOrder } from '../query/ordersQuery';
import { OrderModel } from '../model/orderModel';
import { restaurantPartner } from '../model/restaurantModels';
import { deliveryPartner } from '../model/partnerModels';
import { fetchRestaurants, fetchDeliveryPartners } from '../services/partnerService';
import Helper from '../utills/Helper';
import { exportOrdersToExcel } from '../utills/excelExport';
import { getDeliveredOrderCountByUser } from '../query/ordersQuery';
import OrderMapModal from './OrderMapModal';

const statusItems = ['ALL', 'CANCELLED', 'DELIVERED'];
const scheduleItems: Array<'DAILY' | 'WEEKLY' | 'MONTHLY'> = ['DAILY', 'WEEKLY', 'MONTHLY'];

const STMT_DEFAULT_COLS: Record<StatementType, string[]> = {
  restaurant: ['orderNo', 'date', 'entity', 'status', 'productAmount', 'commission', 'net'],
  partner: ['orderNo', 'date', 'entity', 'status', 'collected', 'deliveryCharge', 'packing', 'surge', 'platformFee'],
};

const stmtToDate = (v: any): Date | null => {
  if (!v) return null;
  try { const d = v instanceof Date ? v : (v?.toDate ? v.toDate() : new Date(v)); return isNaN(d.getTime()) ? null : d; }
  catch { return null; }
};
const stmtFmtDate = (v: any): string => {
  const d = stmtToDate(v);
  return d ? d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'N/A';
};
const stmtLastOrderNo = (o: OrderModel): string => {
  const raw = o.orderNumber?.[0];
  return raw ? String(raw) : (o.id || 'N/A');
};

// Native <input>/<select> styles (raw DOM elements, not RN styles)
const liveStmtDateInputStyle: React.CSSProperties = { flex: 1, height: 40, border: '1px solid #e2e8f0', borderRadius: 8, paddingLeft: 10, fontSize: 13, color: '#E2EDE6', backgroundColor: '#17201B', outline: 'none' };
const liveStmtSelectStyle: React.CSSProperties = { width: '100%', height: 42, border: '1px solid #e2e8f0', borderRadius: 8, paddingLeft: 10, fontSize: 13, color: '#E2EDE6', backgroundColor: '#17201B', cursor: 'pointer', outline: 'none' };

type OrderRowActionsProps = {
  orderId: string;
  onStatusUpdated: () => void;
};

const OrderRowActions: React.FC<OrderRowActionsProps> = ({ orderId, onStatusUpdated }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleStatusUpdate = async (status: string) => {
    setLoading(true);
    try {
      await updateOrder(orderId, { status, active: false });
      setModalVisible(false);
      Alert.alert('Success', `Order marked as ${status} and deactivated`);
      onStatusUpdated();
    } catch (err) {
      Alert.alert('Error', 'Failed to update order.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <View>
      <TouchableOpacity onPress={() => setModalVisible(true)} style={styles.threeDotButton}>
        <span className="material-icons-round" style={{ fontSize: 20, color: '#6B7280', lineHeight: 1 }}>more_vert</span>
      </TouchableOpacity>

      <Modal
        transparent
        visible={modalVisible}
        animationType="fade"
        onRequestClose={() => setModalVisible(false)}
      >
        <Pressable style={styles.modalOverlay} onPress={() => setModalVisible(false)}>
          <View style={styles.menuContainer}>
            <Text style={styles.menuTitle}>Update Status</Text>
            <TouchableOpacity
              disabled={loading}
              onPress={() => handleStatusUpdate('DELIVERED')}
              style={[styles.menuItem, { borderTopWidth: 1, borderTopColor: '#f0f0f0' }]}
            >
              <span className="material-icons-round" style={{ fontSize: 16, color: '#388E3C', marginRight: 8, lineHeight: 1 }}>check_circle</span>
              <Text style={[styles.menuText, { color: '#388E3C' }]}>DELIVERED</Text>
            </TouchableOpacity>
            <TouchableOpacity
              disabled={loading}
              onPress={() => handleStatusUpdate('CANCELLED')}
              style={styles.menuItem}
            >
              <span className="material-icons-round" style={{ fontSize: 16, color: '#D32F2F', marginRight: 8, lineHeight: 1 }}>cancel</span>
              <Text style={[styles.menuText, { color: '#D32F2F' }]}>CANCELLED</Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Modal>
    </View>
  );
};

function OrderCountDisplay({ customerId }: { customerId: string }) {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    async function fetchCount() {
      const countValue = await getDeliveredOrderCountByUser(customerId);
      setCount(countValue);
    }
    fetchCount();
  }, [customerId]);

  return <Text style={styles.tableCell}>{count !== null ? count : '…'}</Text>;
}

const PendingOrdersScreen: React.FC = () => {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const [selectedRestaurant, setSelectedRestaurant] = useState<restaurantPartner | null>(null);
  const [selectedDelivery, setSelectedDelivery] = useState<deliveryPartner | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL');
  const [selectedSchedule, setSelectedSchedule] = useState<'DAILY' | 'WEEKLY' | 'MONTHLY'>('DAILY');
  const [calendar, setCalendar] = useState(new Date());
  const [allRestaurants, setAllRestaurants] = useState<restaurantPartner[]>([]);
  const [allDeliveryPartners, setAllDeliveryPartners] = useState<deliveryPartner[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<string | undefined>>(new Set());
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [mapOrder, setMapOrder] = useState<OrderModel | null>(null);
  const [itemsModalOrder, setItemsModalOrder] = useState<OrderModel | null>(null);

  const {
    orders,
    loading,
    empty,
    errorMessage,
    totalDeliveryCharge,
    totalPacking,
    totalTax,
    totalService,
    totalPrice,
    deliveredCount,
    deliveredTotalPrice,
    deliveredDeliveryCharge,
    deliveredPacking,
    deliveredTax,
    deliveredService,
    setFilters,
  } = usePendingOrders();

  // ── Statement drawer ─────────────────────────────────────────
  const [stmtOpen, setStmtOpen] = useState(false);
  const [stmtType, setStmtType] = useState<StatementType>('restaurant');
  const [stmtFrom, setStmtFrom] = useState(() => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); });
  const [stmtTo, setStmtTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [stmtEntity, setStmtEntity] = useState('ALL');
  const [stmtStatus, setStmtStatus] = useState('DELIVERED');
  const [stmtCommission, setStmtCommission] = useState('10');
  const [stmtCols, setStmtCols] = useState<string[]>(STMT_DEFAULT_COLS.restaurant);

  const stmtW = Math.min(isMobile ? width : 460, width);
  const stmtX = useRef(new Animated.Value(stmtW)).current;
  useEffect(() => {
    if (stmtOpen) {
      stmtX.setValue(stmtW);
      Animated.timing(stmtX, { toValue: 0, duration: 260, easing: Easing.out(Easing.cubic), useNativeDriver: true }).start();
    }
  }, [stmtOpen, stmtW, stmtX]);
  const closeStmt = () => Animated.timing(stmtX, { toValue: stmtW, duration: 200, easing: Easing.in(Easing.cubic), useNativeDriver: true }).start(() => setStmtOpen(false));
  const openStatement = () => { setStmtCols(STMT_DEFAULT_COLS[stmtType]); setStmtEntity('ALL'); setStmtOpen(true); };
  const pickStmtType = (t: StatementType) => { setStmtType(t); setStmtCols(STMT_DEFAULT_COLS[t]); setStmtEntity('ALL'); };
  const toggleStmtCol = (key: string) => setStmtCols(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

  // Build partner-name lookup from the loaded partner list (keyed by every id-ish field).
  const partnerNameById = useMemo(() => {
    const m: Record<string, string> = {};
    allDeliveryPartners.forEach((p: any) => {
      const label = p.storename || p.name || p.pincode;
      [p.pincode, p.storeuid, p.uid, p.partnerId, p.id].forEach((k: any) => { if (k) m[k] = label; });
    });
    return m;
  }, [allDeliveryPartners]);

  const stmtEntities = useMemo(() => {
    const m = new Map<string, string>();
    orders.forEach(o => {
      if (stmtType === 'restaurant') { const name = o.sellerLocation?.name; if (name) m.set(name, name); }
      else if (o.deliveryPartnerId) m.set(o.deliveryPartnerId, partnerNameById[o.deliveryPartnerId] || o.deliveryPartnerId);
    });
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [orders, stmtType, partnerNameById]);

  const handleGenerateStatement = () => {
    const from = new Date(stmtFrom + 'T00:00:00');
    const to = new Date(stmtTo + 'T23:59:59');
    const pct = parseFloat(stmtCommission) || 0;
    const rows: StatementOrder[] = [];
    orders.forEach(o => {
      const d = stmtToDate(o.createdAt); if (!d || d < from || d > to) return;
      const st = (o.status || '').toUpperCase();
      if (stmtStatus !== 'ALL' && st !== stmtStatus) return;
      const entityId = stmtType === 'restaurant' ? (o.sellerLocation?.name || '') : (o.deliveryPartnerId || '');
      if (!entityId) return;
      if (stmtEntity !== 'ALL' && entityId !== stmtEntity) return;
      const ch: any = o.charges || {};
      rows.push({
        orderNo: stmtLastOrderNo(o),
        date: stmtFmtDate(o.createdAt),
        entity: stmtType === 'restaurant'
          ? (o.sellerLocation?.name || '—')
          : (partnerNameById[o.deliveryPartnerId || ''] || o.deliveryPartnerId || '—'),
        status: st,
        productAmount: o.totalPrice || 0,
        deliveryCharge: ch.deliveryCharge || 0,
        packing: ch.packing || 0,
        surge: ch.surge || 0,
        platformFee: ch.platformFee ?? ch.service ?? 0,
        collected: (o as any).customerPaid ?? o.payment?.amount ?? 0,
      });
    });
    if (rows.length === 0) { window.alert('No orders match the selected filters.'); return; }
    if (stmtCols.length === 0) { window.alert('Select at least one column.'); return; }
    const entityName = stmtEntity === 'ALL' ? 'All' : (stmtEntities.find(e => e.id === stmtEntity)?.name || stmtEntity);
    generateStatementPdf({
      type: stmtType,
      heading: stmtType === 'restaurant' ? 'Restaurant Statement' : 'Delivery Partner Statement',
      entityName,
      rangeLabel: `${stmtFrom} to ${stmtTo}`,
      statusLabel: stmtStatus,
      generatedAt: new Date().toLocaleString('en-IN'),
      commissionPct: pct,
      columns: stmtCols,
      orders: rows,
    });
    closeStmt();
  };

  const getStatusColor = (status?: string) => {
    switch ((status || '').toUpperCase()) {
      case 'PENDING': return '#F59E0B';
      case 'CANCELLED': return '#EF4444';
      case 'DELIVERED': return '#10B981';
      case 'CONFIRMED': return '#3B82F6';
      default: return '#6B7280';
    }
  };

  useEffect(() => {
    const loadPartners = async () => {
      const restaurants = await fetchRestaurants();
      setAllRestaurants([{ storeuid: '', storename: 'All Restaurants' }, ...restaurants]);
      const deliveryPartners = await fetchDeliveryPartners();
      setAllDeliveryPartners([{ pincode: '', storename: 'All Partners' }, ...deliveryPartners]);
    };
    loadPartners();
  }, []);

  useEffect(() => {
    const restaurantId = selectedRestaurant?.storeuid || null;
    const deliveryPartnerId = selectedDelivery?.pincode || null;
    setFilters(
      selectedStatus === 'ALL' ? null : selectedStatus,
      restaurantId,
      deliveryPartnerId,
      selectedSchedule,
      calendar
    );
  }, [selectedRestaurant, selectedDelivery, selectedStatus, selectedSchedule, calendar]);

  useEffect(() => {
    if (errorMessage) Alert.alert('Error', errorMessage);
  }, [errorMessage]);

  const changeDate = (amount: number) => {
    const newDate = new Date(calendar);
    switch (selectedSchedule) {
      case 'DAILY': newDate.setDate(newDate.getDate() + amount); break;
      case 'WEEKLY': newDate.setDate(newDate.getDate() + amount * 7); break;
      case 'MONTHLY': newDate.setMonth(newDate.getMonth() + amount); break;
    }
    setCalendar(newDate);
  };

  const formatCurrentDate = () => Helper.formatDate(calendar, selectedSchedule);

  const isLastDate = () => {
    const today = new Date();
    switch (selectedSchedule) {
      case 'DAILY':
        return calendar.getFullYear() === today.getFullYear() &&
               calendar.getMonth() === today.getMonth() &&
               calendar.getDate() === today.getDate();
      case 'WEEKLY': {
        const startOfWeek = (d: Date) => { const c = new Date(d); c.setDate(c.getDate() - c.getDay()); c.setHours(0,0,0,0); return c; };
        return startOfWeek(calendar).getTime() === startOfWeek(today).getTime();
      }
      case 'MONTHLY':
        return calendar.getFullYear() === today.getFullYear() &&
               calendar.getMonth() === today.getMonth();
    }
  };

  const toggleExpand = (orderId: string | undefined) => {
    const newSet = new Set(expandedOrderIds);
    if (newSet.has(orderId)) {
      newSet.delete(orderId);
    } else {
      newSet.add(orderId);
    }
    setExpandedOrderIds(newSet);
  };

  const filteredOrders = orders.filter((order) =>
    !searchQuery ||
    (order.orderNumber?.some((num) =>
      num.toLowerCase().includes(searchQuery.toLowerCase())
    ))
  );

  const activeFilterCount = [
    selectedStatus !== 'ALL',
    !!selectedRestaurant?.storeuid,
    !!selectedDelivery?.pincode,
  ].filter(Boolean).length;

  const handleResetFilters = () => {
    setSelectedStatus('ALL');
    setSelectedRestaurant(allRestaurants[0] ?? null);
    setSelectedDelivery(allDeliveryPartners[0] ?? null);
    setSelectedSchedule('DAILY');
  };

  const renderFilterModal = () => (
    <Modal
      transparent
      visible={filterModalVisible}
      animationType="slide"
      onRequestClose={() => setFilterModalVisible(false)}
    >
      <Pressable style={styles.filterModalOverlay} onPress={() => setFilterModalVisible(false)}>
        <Pressable style={styles.filterModalSheet} onPress={() => {}}>
          <View style={styles.filterModalHeader}>
            <Text style={styles.filterModalTitle}>Filters</Text>
            <TouchableOpacity onPress={handleResetFilters} style={styles.resetButton}>
              <Text style={styles.resetButtonText}>Reset</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => setFilterModalVisible(false)} style={styles.filterCloseBtn}>
              <span className="material-icons-round" style={{ fontSize: 18, color: '#6B7280', lineHeight: 1 }}>close</span>
            </TouchableOpacity>
          </View>

          <ScrollView showsVerticalScrollIndicator={false}>
            <Text style={styles.filterLabel}>Schedule</Text>
            <View style={styles.filterChipRow}>
              {scheduleItems.map((s) => (
                <TouchableOpacity
                  key={s}
                  style={[styles.filterChip, selectedSchedule === s && styles.filterChipActive]}
                  onPress={() => setSelectedSchedule(s as typeof selectedSchedule)}
                >
                  <Text style={[styles.filterChipText, selectedSchedule === s && styles.filterChipTextActive]}>{s}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={styles.filterLabel}>Status</Text>
            <View style={styles.filterChipRow}>
              {statusItems.map((s) => (
                <TouchableOpacity
                  key={s}
                  style={[styles.filterChip, selectedStatus === s && styles.filterChipActive]}
                  onPress={() => setSelectedStatus(s)}
                >
                  <Text style={[styles.filterChipText, selectedStatus === s && styles.filterChipTextActive]}>{s}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={styles.filterLabel}>Restaurant</Text>
            <View style={styles.filterPickerWrap}>
              <Picker
                selectedValue={selectedRestaurant?.storeuid ?? ''}
                style={styles.filterPicker}
                onValueChange={(value, index) => setSelectedRestaurant(allRestaurants[index])}
              >
                {allRestaurants.map((rest) => (
                  <Picker.Item key={rest.storeuid || 'all'} label={rest.storename} value={rest.storeuid} />
                ))}
              </Picker>
            </View>

            <Text style={styles.filterLabel}>Delivery Partner</Text>
            <View style={styles.filterPickerWrap}>
              <Picker
                selectedValue={selectedDelivery?.pincode ?? ''}
                style={styles.filterPicker}
                onValueChange={(value, index) => setSelectedDelivery(allDeliveryPartners[index])}
              >
                {allDeliveryPartners.map((partner) => (
                  <Picker.Item key={partner.pincode || 'all'} label={partner.storename} value={partner.pincode} />
                ))}
              </Picker>
            </View>
          </ScrollView>

          <TouchableOpacity style={styles.applyButton} onPress={() => setFilterModalVisible(false)}>
            <Text style={styles.applyButtonText}>Apply Filters</Text>
          </TouchableOpacity>
        </Pressable>
      </Pressable>
    </Modal>
  );

  const renderToolbar = () => (
    <View style={styles.toolbar}>
      {/* Title */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginRight: 'auto' as any }}>
        <span className="material-icons-round" style={{ fontSize: 18, color: '#4ECDC4', lineHeight: 1 }}>receipt_long</span>
        <View>
          <Text style={{ fontSize: 15, fontWeight: '800', color: '#fff' }}>Orders</Text>
          <Text style={{ fontSize: 11, color: '#8BAAB8', marginTop: 1 }}>Daily order activity</Text>
        </View>
      </View>
      {/* Date navigation inline */}
      <TouchableOpacity style={styles.navButton} onPress={() => changeDate(-1)}>
        <Text style={styles.navButtonText}>‹</Text>
      </TouchableOpacity>
      <View style={styles.dateChip}>
        <Text style={styles.currentDate}>{formatCurrentDate()}</Text>
      </View>
      <TouchableOpacity
        style={[styles.navButton, isLastDate() && { opacity: 0.3 }]}
        onPress={() => changeDate(1)}
        disabled={isLastDate()}
      >
        <Text style={styles.navButtonText}>›</Text>
      </TouchableOpacity>

      {/* Search */}
      <View style={[styles.searchWrap, { flex: 1 }]}>
        <span className="material-icons-round" style={{ fontSize: 15, color: '#8BAAB8', lineHeight: 1, marginRight: 6 }}>search</span>
        <TextInput
          placeholder="Search orders…"
          placeholderTextColor="#8BAAB8"
          value={searchQuery}
          onChangeText={setSearchQuery}
          style={styles.toolbarSearch}
          autoCapitalize="none"
          autoCorrect={false}
          clearButtonMode="while-editing"
        />
      </View>

      <TouchableOpacity style={styles.filterButton} onPress={() => setFilterModalVisible(true)}>
        <span className="material-icons-round" style={{ fontSize: 18, color: '#8BAAB8', lineHeight: 1 }}>tune</span>
        {activeFilterCount > 0 && (
          <View style={styles.filterBadge}>
            <Text style={styles.filterBadgeText}>{activeFilterCount}</Text>
          </View>
        )}
      </TouchableOpacity>
      <button
        style={{ height: 34, borderRadius: 7, backgroundColor: '#21C98E', color: '#fff', paddingLeft: 10, paddingRight: 10, cursor: 'pointer', border: 'none', fontSize: 11, fontWeight: '700', whiteSpace: 'nowrap', letterSpacing: 0.3, display: 'flex', alignItems: 'center' }}
        onClick={() => exportOrdersToExcel(orders)}
      >
        <span className="material-icons-round" style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 3 }}>download</span>Export
      </button>
      <button
        style={{ height: 34, borderRadius: 7, backgroundColor: '#21808D', color: '#fff', paddingLeft: 10, paddingRight: 10, cursor: 'pointer', border: 'none', fontSize: 11, fontWeight: '700', whiteSpace: 'nowrap', letterSpacing: 0.3, display: 'flex', alignItems: 'center' }}
        onClick={openStatement}
      >
        <span className="material-icons-round" style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 3 }}>description</span>Statement
      </button>
    </View>
  );

  // Card layout for mobile
  const renderOrderCard = (order: OrderModel) => {
    const isExpanded = expandedOrderIds.has(order.id);
    const statusColor = getStatusColor(order.status);
    return (
      <View key={order.id ?? Math.random().toString()} style={styles.card}>
        <View style={[styles.cardAccent, { backgroundColor: statusColor }]} />
        <View style={styles.cardInner}>
          <View style={styles.cardHeader}>
            <Text style={styles.cardOrderNum}>
              #{order.orderNumber?.[0] ?? 'N/A'}
            </Text>
            <View style={[styles.statusPill, { backgroundColor: statusColor + '18', borderColor: statusColor + '40' }]}>
              <View style={[styles.statusDot, { backgroundColor: statusColor }]} />
              <Text style={[styles.statusPillText, { color: statusColor }]}>{order.status ?? 'N/A'}</Text>
            </View>
          </View>

          <View style={styles.cardGrid}>
            <View style={styles.cardGridItem}>
              <Text style={styles.cardLabel}>Contact</Text>
              <Text style={styles.cardValue}>{order.userLocation?.secondaryContact ?? 'N/A'}</Text>
            </View>
            <View style={styles.cardGridItem}>
              <Text style={styles.cardLabel}>Distance</Text>
              <Text style={styles.cardValue}>{order.distance ?? '0'} km</Text>
            </View>
            <View style={styles.cardGridItem}>
              <Text style={styles.cardLabel}>Total</Text>
              <Text style={[styles.cardValue, { color: '#10B981', fontWeight: '700' }]}>₹{order.totalPrice?.toFixed(2) ?? '0.00'}</Text>
            </View>
            <View style={styles.cardGridItem}>
              <Text style={styles.cardLabel}>Charges</Text>
              <Text style={styles.cardValue}>
                ₹{((order.charges?.service || 0) + (order.charges?.deliveryCharge || 0) + (order.charges?.packing || 0)).toFixed(2)}
              </Text>
            </View>
          </View>

          <View style={styles.cardRow}>
            <Text style={styles.cardLabel}>Customer</Text>
            <Text style={styles.cardValue}>{order.userLocation?.name ?? 'N/A'}</Text>
          </View>
          <View style={styles.cardRow}>
            <Text style={styles.cardLabel}>Pickup</Text>
            <Text style={styles.cardValue} numberOfLines={1}>{order.sellerLocation?.name ?? 'N/A'}</Text>
          </View>
          <View style={styles.cardRow}>
            <Text style={styles.cardLabel}>Drop</Text>
            <Text style={styles.cardValue} numberOfLines={2}>{order.userLocation?.address ?? 'N/A'}</Text>
          </View>
          <View style={styles.cardRow}>
            <Text style={styles.cardLabel}>Created</Text>
            <Text style={styles.cardValue}>
              {order.createdAt ? new Date(order.createdAt).toLocaleString() : 'N/A'}
            </Text>
          </View>

          <View style={styles.cardActions}>
            <TouchableOpacity style={styles.expandButton} onPress={() => setItemsModalOrder(order)}>
              <span className="material-icons-round" style={{ fontSize: 14, color: '#3B82F6', lineHeight: 1, marginRight: 3 }}>shopping_bag</span>
              <Text style={styles.expandButtonText}>Items</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.mapButton} onPress={() => setMapOrder(order)}>
              <span className="material-icons-round" style={{ fontSize: 16, color: '#fff', lineHeight: 1 }}>map</span>
              <Text style={styles.mapButtonText}>Map</Text>
            </TouchableOpacity>
            <OrderRowActions
              orderId={order.id ?? ''}
              onStatusUpdated={() => setFilters(null, null, null, selectedSchedule, calendar)}
            />
          </View>
        </View>
      </View>
    );
  };

  // Table row layout for desktop
  const renderOrderRow = (order: OrderModel, index: number) => {
    const isExpanded = expandedOrderIds.has(order.id);
    const statusColor = getStatusColor(order.status);
    return (
      <View key={order.id ?? Math.random().toString()}>
        <View style={[styles.tableRow, index % 2 === 0 && styles.tableRowAlt]}>
          <Text style={[styles.tableCell, styles.cellOrder, { color: '#1D4ED8', fontWeight: '600' }]}>
            #{order.orderNumber?.[0] ?? ''}
          </Text>
          <Text style={[styles.tableCell, styles.cellOrder]} numberOfLines={1}>
            {order.userLocation?.name ?? '—'}
          </Text>
          <Text style={[styles.tableCell, styles.cellOrder]}>
            {order.userLocation?.secondaryContact}
          </Text>
          <Text style={[styles.tableCell, styles.cellOrder]} numberOfLines={1}>
            {order.sellerLocation?.name}
          </Text>
          <Text style={[styles.tableCell, styles.cellOrder]} numberOfLines={1}>
            {order.userLocation?.address}
          </Text>
          <View style={[styles.cellStatus, { alignItems: 'center', justifyContent: 'center' }]}>
            <View style={[styles.statusPill, { backgroundColor: statusColor + '18', borderColor: statusColor + '40' }]}>
              <View style={[styles.statusDot, { backgroundColor: statusColor }]} />
              <Text style={[styles.statusPillText, { color: statusColor }]}>{order.status ?? 'N/A'}</Text>
            </View>
          </View>
          <Text style={[styles.tableCell, styles.cellDate]}>
            {order.createdAt ? new Date(order.createdAt).toLocaleString() : 'N/A'}
          </Text>
          <View style={[styles.cellItems, { alignItems: 'center' }]}>
            <OrderCountDisplay customerId={order.customerId} />
          </View>
          <Text style={[styles.tableCell, styles.cellPrice, { color: '#10B981', fontWeight: '600' }]}>
            ₹{order.totalPrice?.toFixed(2) ?? '0.00'}
          </Text>
          <Text style={[styles.tableCell, styles.cellPrice]}>
            ₹{((order.charges?.service || 0) + (order.charges?.deliveryCharge || 0) + (order.charges?.packing || 0)).toFixed(2)}
          </Text>
          <Text style={[styles.tableCell, styles.cellPrice]}>
            {order.distance ?? '0'}
          </Text>
          <TouchableOpacity
            style={[styles.cellExpand, { alignItems: 'center', justifyContent: 'center' }]}
            onPress={() => setItemsModalOrder(order)}
          >
            <span className="material-icons-round" style={{ fontSize: 18, color: '#3B82F6', lineHeight: 1 }}>shopping_bag</span>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.cellExpand, { alignItems: 'center', justifyContent: 'center' }]}
            onPress={() => setMapOrder(order)}
          >
            <span className="material-icons-round" style={{ fontSize: 18, color: '#3B82F6', lineHeight: 1 }}>map</span>
          </TouchableOpacity>
          <View style={[styles.cellExpand, { alignItems: 'center', justifyContent: 'center' }]}>
            <OrderRowActions
              orderId={order.id ?? ''}
              onStatusUpdated={() => setFilters(null, null, null, selectedSchedule, calendar)}
            />
          </View>
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {/* Summary — merged cards */}
      <View style={styles.summaryRow}>
        {/* Delivery charge */}
        <View style={[styles.sCard, { borderTopColor: '#3B82F6' }]}>
          <Text style={styles.sCardLabel}>Delivery</Text>
          <Text style={[styles.sCardValue, { color: '#3B82F6' }]}>₹{totalDeliveryCharge.toFixed(2)}</Text>
          <View style={styles.sCardDivider} />
          <Text style={styles.sCardSubLabel}>Delivered</Text>
          <Text style={[styles.sCardSubValue, { color: '#059669' }]}>₹{deliveredDeliveryCharge.toFixed(2)}</Text>
        </View>

        {/* Packing */}
        <View style={[styles.sCard, { borderTopColor: '#F59E0B' }]}>
          <Text style={styles.sCardLabel}>Packing</Text>
          <Text style={[styles.sCardValue, { color: '#F59E0B' }]}>₹{totalPacking.toFixed(2)}</Text>
          <View style={styles.sCardDivider} />
          <Text style={styles.sCardSubLabel}>Delivered</Text>
          <Text style={[styles.sCardSubValue, { color: '#059669' }]}>₹{deliveredPacking.toFixed(2)}</Text>
        </View>

        {/* Tax + Service */}
        <View style={[styles.sCard, { borderTopColor: '#8B5CF6' }]}>
          <Text style={styles.sCardLabel}>Tax+Svc</Text>
          <Text style={[styles.sCardValue, { color: '#8B5CF6' }]}>₹{(totalTax + totalService).toFixed(2)}</Text>
          <View style={styles.sCardDivider} />
          <Text style={styles.sCardSubLabel}>Delivered</Text>
          <Text style={[styles.sCardSubValue, { color: '#059669' }]}>₹{(deliveredTax + deliveredService).toFixed(2)}</Text>
        </View>

        {/* Orders count */}
        <View style={[styles.sCard, { borderTopColor: '#10B981' }]}>
          <Text style={styles.sCardLabel}>Total Orders</Text>
          <Text style={[styles.sCardValue, { color: '#21808D' }]}>{orders.length}</Text>
          <View style={styles.sCardDivider} />
          <Text style={styles.sCardSubLabel}>Delivered</Text>
          <Text style={[styles.sCardSubValue, { color: '#10B981' }]}>{deliveredCount}</Text>
        </View>

        {/* Product Amount */}
        <View style={[styles.sCard, { borderTopColor: '#EC4899' }]}>
          <Text style={styles.sCardLabel}>Product Amt</Text>
          <Text style={[styles.sCardValue, { color: '#EC4899' }]}>₹{totalPrice.toFixed(2)}</Text>
          <View style={styles.sCardDivider} />
          <Text style={styles.sCardSubLabel}>Delivered</Text>
          <Text style={[styles.sCardSubValue, { color: '#059669' }]}>₹{deliveredTotalPrice.toFixed(2)}</Text>
        </View>

        {/* Revenue */}
        <View style={[styles.sCard, styles.sCardDark]}>
          <Text style={[styles.sCardLabel, { color: 'rgba(255,255,255,0.5)' }]}>All Rev.</Text>
          <Text style={[styles.sCardValue, { color: '#fff' }]}>
            ₹{(totalDeliveryCharge + totalPacking + totalTax + totalService + totalPrice).toFixed(2)}
          </Text>
          <View style={[styles.sCardDivider, { backgroundColor: 'rgba(255,255,255,0.12)' }]} />
          <Text style={[styles.sCardSubLabel, { color: 'rgba(255,255,255,0.5)' }]}>Del+pkg+tx+srvc+prdct</Text>
          <Text style={[styles.sCardSubValue, { color: '#6EE7B7' }]}>
            ₹{(deliveredDeliveryCharge + deliveredPacking + deliveredTax + deliveredService + deliveredTotalPrice).toFixed(2)}
          </Text>
        </View>
      </View>

      {/* Toolbar */}
      {renderToolbar()}
      {renderFilterModal()}

      {/* Statement drawer */}
      <Modal visible={stmtOpen} animationType="fade" transparent onRequestClose={closeStmt}>
        <View style={sd.root}>
          <Pressable style={sd.backdrop} onPress={closeStmt} />
          <Animated.View style={[sd.drawer, { width: stmtW, transform: [{ translateX: stmtX }] }]}>
            <View style={sd.header}>
              <TouchableOpacity style={sd.closeBtn} onPress={closeStmt}><Text style={sd.closeTxt}>✕</Text></TouchableOpacity>
              <Text style={sd.title}>Download Statement</Text>
              <View style={{ width: 32 }} />
            </View>
            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 18 }}>
              <Text style={sd.label}>Statement type</Text>
              <View style={sd.segRow}>
                {([['restaurant', '🍽️ Restaurant'], ['partner', '🛵 Delivery Partner']] as [StatementType, string][]).map(([t, lbl]) => (
                  <TouchableOpacity key={t} style={[sd.seg, stmtType === t && sd.segOn]} onPress={() => pickStmtType(t)}>
                    <Text style={[sd.segTxt, stmtType === t && sd.segTxtOn]}>{lbl}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              <Text style={sd.label}>Date range</Text>
              <View style={sd.row2}>
                <input type="date" value={stmtFrom} onChange={(e: any) => setStmtFrom(e.target.value)} style={liveStmtDateInputStyle} />
                <Text style={{ color: '#7A9A80', fontWeight: '700' }}>–</Text>
                <input type="date" value={stmtTo} onChange={(e: any) => setStmtTo(e.target.value)} style={liveStmtDateInputStyle} />
              </View>

              <Text style={sd.label}>{stmtType === 'restaurant' ? 'Restaurant' : 'Delivery partner'}</Text>
              <select value={stmtEntity} onChange={(e: any) => setStmtEntity(e.target.value)} style={liveStmtSelectStyle}>
                <option value="ALL">All {stmtType === 'restaurant' ? 'restaurants' : 'partners'}</option>
                {stmtEntities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>

              <Text style={sd.label}>Status</Text>
              <select value={stmtStatus} onChange={(e: any) => setStmtStatus(e.target.value)} style={liveStmtSelectStyle}>
                {statusItems.map(s => <option key={s} value={s}>{s === 'ALL' ? 'All statuses' : s}</option>)}
              </select>

              {stmtType === 'restaurant' && (
                <>
                  <Text style={sd.label}>Commission %</Text>
                  <TextInput style={sd.textInput} value={stmtCommission} onChangeText={setStmtCommission} keyboardType="decimal-pad" placeholder="e.g. 10" placeholderTextColor="#94a3b8" />
                </>
              )}

              <Text style={sd.label}>Columns in PDF</Text>
              <View style={sd.colGrid}>
                {STATEMENT_COLUMNS.map(c => {
                  const on = stmtCols.includes(c.key);
                  return (
                    <TouchableOpacity key={c.key} style={[sd.colChip, on && sd.colChipOn]} onPress={() => toggleStmtCol(c.key)}>
                      <Text style={[sd.colChipTxt, on && sd.colChipTxtOn]}>{on ? '✓ ' : ''}{c.label}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>

              <Text style={sd.note}>
                PDF includes a totals row + a Summary block — {stmtType === 'partner'
                  ? 'collected, delivery, packing, surge & platform sums.'
                  : 'total product amount, commission & net payable.'}
              </Text>
            </ScrollView>
            <View style={sd.footer}>
              <TouchableOpacity style={sd.downloadBtn} onPress={handleGenerateStatement}>
                <Text style={sd.downloadTxt}>⬇ Download PDF</Text>
              </TouchableOpacity>
            </View>
          </Animated.View>
        </View>
      </Modal>

      <OrderMapModal
        visible={!!mapOrder}
        order={mapOrder}
        onClose={() => setMapOrder(null)}
      />

      {/* Content */}
      {loading ? (
        <ActivityIndicator size="large" color="#1D4ED8" style={{ marginTop: 40 }} />
      ) : empty ? (
        <View style={styles.emptyBox}>
          <span className="material-icons-round" style={{ fontSize: 48, color: '#6A8A70', lineHeight: 1 }}>inbox</span>
          <Text style={styles.emptyText}>No orders found</Text>
        </View>
      ) : isMobile ? (
        <ScrollView style={{ marginTop: 4 }} contentContainerStyle={{ paddingBottom: 24 }}>
          {filteredOrders.map(renderOrderCard)}
        </ScrollView>
      ) : (
        <View style={{ flex: 1, marginTop: 4 }}>
          {/* Sticky table header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.headerText, styles.cellOrder]}>Order #</Text>
            <Text style={[styles.headerText, styles.cellOrder]}>Customer</Text>
            <Text style={[styles.headerText, styles.cellOrder]}>Contact</Text>
            <Text style={[styles.headerText, styles.cellOrder]}>Pickup</Text>
            <Text style={[styles.headerText, styles.cellOrder]}>Drop</Text>
            <Text style={[styles.headerText, styles.cellStatus]}>Status</Text>
            <Text style={[styles.headerText, styles.cellDate]}>Created</Text>
            <Text style={[styles.headerText, styles.cellItems]}>Orders</Text>
            <Text style={[styles.headerText, styles.cellPrice]}>Price</Text>
            <Text style={[styles.headerText, styles.cellPrice]}>Charges</Text>
            <Text style={[styles.headerText, styles.cellPrice]}>Dist.</Text>
            <Text style={[styles.headerText, styles.cellExpand]}>Items</Text>
            <Text style={[styles.headerText, styles.cellExpand]}>Map</Text>
            <Text style={[styles.headerText, styles.cellExpand]}>Act.</Text>
          </View>
          <ScrollView contentContainerStyle={{ paddingBottom: 24 }}>
            {filteredOrders.map((o, i) => renderOrderRow(o, i))}
          </ScrollView>
        </View>
      )}

      {/* Items Modal */}
      <Modal
        transparent
        visible={!!itemsModalOrder}
        animationType="fade"
        onRequestClose={() => setItemsModalOrder(null)}
      >
        <Pressable style={styles.itemsModalOverlay} onPress={() => setItemsModalOrder(null)}>
          <Pressable style={styles.itemsModalSheet} onPress={() => {}}>
            {/* Header */}
            <View style={styles.itemsModalHeader}>
              <View style={{ flex: 1 }}>
                <Text style={styles.itemsModalTitle}>
                  #{itemsModalOrder?.orderNumber?.[0] ?? 'Order'}
                </Text>
                <Text style={styles.itemsModalSub}>
                  {itemsModalOrder?.sellerLocation?.name ?? ''}
                </Text>
              </View>
              <TouchableOpacity onPress={() => setItemsModalOrder(null)} style={styles.itemsModalClose}>
                <span className="material-icons-round" style={{ fontSize: 22, color: '#6B7280', lineHeight: 1 }}>close</span>
              </TouchableOpacity>
            </View>

            {/* Column headers */}
            <View style={styles.itemsModalTableHeader}>
              <Text style={[styles.itemsModalHeaderCell, { flex: 4 }]}>Product</Text>
              <Text style={[styles.itemsModalHeaderCell, { flex: 1, textAlign: 'center' }]}>Qty</Text>
              <Text style={[styles.itemsModalHeaderCell, { flex: 1, textAlign: 'right' }]}>Price</Text>
              <Text style={[styles.itemsModalHeaderCell, { flex: 1, textAlign: 'right' }]}>Total</Text>
            </View>

            {/* Items list */}
            <ScrollView style={{ maxHeight: 380 }} showsVerticalScrollIndicator={false}>
              {itemsModalOrder?.orderItems?.map((item, idx) => (
                <View
                  key={idx}
                  style={[styles.itemsModalRow, idx % 2 === 0 && { backgroundColor: '#F9FAFB' }]}
                >
                  {/* Image + Name */}
                  <View style={{ flex: 4, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    {item.imageUrl ? (
                      <Image
                        source={{ uri: item.imageUrl }}
                        style={styles.itemsModalImage}
                        resizeMode="cover"
                      />
                    ) : (
                      <View style={styles.itemsModalImagePlaceholder}>
                        <span className="material-icons-round" style={{ fontSize: 20, color: '#CBD5E1', lineHeight: 1 }}>image</span>
                      </View>
                    )}
                    <View style={{ flex: 1 }}>
                      <Text style={styles.itemsModalProductName} numberOfLines={2}>
                        {item.productName ?? 'No Name'}
                      </Text>
                      {item.unit ? (
                        <Text style={styles.itemsModalUnit}>{item.unit}</Text>
                      ) : null}
                    </View>
                  </View>
                  <Text style={[styles.itemsModalCell, { flex: 1, textAlign: 'center' }]}>
                    ×{item.quantity}
                  </Text>
                  <Text style={[styles.itemsModalCell, { flex: 1, textAlign: 'right' }]}>
                    ₹{item.price?.toFixed(2) ?? '0.00'}
                  </Text>
                  <Text style={[styles.itemsModalCell, { flex: 1, textAlign: 'right', fontWeight: '700', color: '#10B981' }]}>
                    ₹{((item.price ?? 0) * (item.quantity ?? 1)).toFixed(2)}
                  </Text>
                </View>
              ))}
            </ScrollView>

            {/* Footer total */}
            <View style={styles.itemsModalFooter}>
              <Text style={styles.itemsModalFooterLabel}>Order Total</Text>
              <Text style={styles.itemsModalFooterValue}>
                ₹{itemsModalOrder?.totalPrice?.toFixed(2) ?? '0.00'}
              </Text>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
};

export default PendingOrdersScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0E1512',
    padding: 10,
  },
  // Toolbar
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: -10,
    marginTop: -10,
    marginBottom: 8,
    gap: 6,
    backgroundColor: '#0A1712',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(33,128,141,0.18)',
    flexWrap: 'wrap',
  },
  searchWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: 7,
    borderWidth: 1,
    borderColor: 'rgba(33,128,141,0.25)',
    paddingHorizontal: 8,
    height: 34,
    minWidth: 140,
  },
  searchIcon: {
    fontSize: 12,
    marginRight: 6,
  },
  toolbarSearch: {
    flex: 1,
    height: 34,
    fontSize: 12,
    color: '#e2f0f2',
    outlineStyle: 'none',
  } as any,
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 34,
    width: 34,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: 7,
    borderWidth: 1,
    borderColor: 'rgba(33,128,141,0.3)',
  },
  filterButtonIcon: {
    fontSize: 15,
    color: '#A8C4AF',
  },
  filterBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: '#EF4444',
    borderRadius: 8,
    minWidth: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 3,
  },
  filterBadgeText: {
    color: '#fff',
    fontSize: 9,
    fontWeight: '700',
  },
  // Filter modal
  filterModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'flex-end',
  },
  filterModalSheet: {
    backgroundColor: '#17201B',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingHorizontal: 18,
    paddingTop: 14,
    paddingBottom: 28,
    maxHeight: '85%',
  },
  filterModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  filterModalTitle: {
    flex: 1,
    fontSize: 15,
    fontWeight: '700',
    color: '#111827',
  },
  resetButton: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    backgroundColor: '#0E1512',
    marginRight: 8,
  },
  resetButtonText: {
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '600',
  },
  filterCloseBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#0E1512',
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterCloseBtnText: {
    fontSize: 12,
    color: '#A8C4AF',
    fontWeight: '700',
  },
  filterLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: '#6A8A70',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  filterChipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 16,
  },
  filterChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: '#F9FAFB',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.09)',
  },
  filterChipActive: {
    backgroundColor: '#1D4ED8',
    borderColor: '#1D4ED8',
  },
  filterChipText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#A8C4AF',
  },
  filterChipTextActive: {
    color: '#fff',
  },
  filterPickerWrap: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.09)',
    backgroundColor: '#F9FAFB',
    marginBottom: 16,
    overflow: 'hidden',
  },
  filterPicker: {
    height: 44,
  },
  applyButton: {
    backgroundColor: '#1D4ED8',
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: 'center',
    marginTop: 6,
  },
  applyButtonText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '700',
  },
  // Summary row
  summaryRow: {
    flexDirection: 'row',
    gap: 5,
    marginBottom: 8,
  },
  summaryRowContent: {
    flexDirection: 'row',
    gap: 5,
  },
  sCard: {
    flex: 1,
    backgroundColor: '#17201B',
    borderRadius: 7,
    paddingVertical: 8,
    paddingHorizontal: 6,
    borderTopWidth: 3,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  sCardDivider: {
    width: '80%',
    height: 1,
    backgroundColor: '#F1F5F9',
    marginVertical: 5,
  },
  sCardSubLabel: {
    fontSize: 7,
    color: '#6A8A70',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginBottom: 1,
    textAlign: 'center',
  },
  sCardSubValue: {
    fontSize: 11,
    fontWeight: '700',
    textAlign: 'center',
  },
  sCardFormula: {
    fontSize: 7,
    color: '#6A8A70',
    fontWeight: '500',
    textAlign: 'center',
    marginBottom: 2,
    letterSpacing: 0.2,
  },
  sCardDark: {
    backgroundColor: '#111827',
    borderTopWidth: 0,
  },
  sCardGreen: {
    backgroundColor: '#064E3B',
    borderTopWidth: 0,
  },
  sCardLabel: {
    fontSize: 8,
    color: '#6A8A70',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 2,
    textAlign: 'center',
  },
  sCardValue: {
    fontSize: 12,
    fontWeight: '800',
    color: '#111827',
    textAlign: 'center',
  },
  sCardSep: {
    width: 1,
    backgroundColor: '#E5E7EB',
    marginHorizontal: 2,
    borderRadius: 1,
  },
  // Date navigation
  dateNavigation: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  navButton: {
    paddingVertical: 5,
    paddingHorizontal: 12,
    backgroundColor: 'rgba(33,128,141,0.2)',
    borderRadius: 7,
    borderWidth: 1,
    borderColor: 'rgba(33,128,141,0.4)',
  },
  navButtonText: {
    color: '#4ECDC4',
    fontWeight: '700',
    fontSize: 14,
  },
  dateChip: {
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderRadius: 7,
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderWidth: 1,
    borderColor: 'rgba(33,128,141,0.3)',
  },
  currentDate: {
    fontSize: 12,
    fontWeight: '600',
    color: '#e2f0f2',
  },
  orderCountRow: {
    marginBottom: 6,
  },
  orderCountText: {
    fontSize: 10,
    color: '#6A8A70',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  // Desktop table
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#1e293b',
    paddingVertical: 9,
    paddingHorizontal: 4,
    borderRadius: 8,
    marginBottom: 2,
  },
  headerText: {
    color: '#94A3B8',
    fontWeight: '700',
    textAlign: 'center',
    fontSize: 9,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tableRow: {
    flexDirection: 'row',
    backgroundColor: '#17201B',
    borderBottomWidth: 1,
    borderColor: 'rgba(255,255,255,0.07)',
    paddingVertical: 9,
    paddingHorizontal: 4,
    alignItems: 'center',
  },
  tableRowAlt: {
    backgroundColor: '#141E18',
  },
  tableCell: {
    fontSize: 11,
    color: '#A8C4AF',
    textAlign: 'center',
    paddingHorizontal: 4,
  },
  cellOrder: {
    flex: 2,
    textAlign: 'left',
  },
  cellStatus: {
    flex: 1,
  },
  cellDate: {
    flex: 2,
    fontSize: 10,
  },
  cellItems: {
    flex: 1,
  },
  cellPrice: {
    flex: 1,
    fontSize: 11,
  },
  cellExpand: {
    flex: 0.6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconBtn: {
    width: 22,
    height: 22,
    borderRadius: 4,
    backgroundColor: '#EFF6FF',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#BFDBFE',
  },
  // Status pill
  statusPill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 20,
    borderWidth: 1,
    gap: 4,
  },
  statusDot: {
    width: 5,
    height: 5,
    borderRadius: 3,
  },
  statusPillText: {
    fontSize: 9,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  // Mobile card
  card: {
    backgroundColor: '#17201B',
    borderRadius: 10,
    marginBottom: 8,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 3,
    elevation: 2,
    flexDirection: 'row',
  },
  cardAccent: {
    width: 4,
  },
  cardInner: {
    flex: 1,
    padding: 12,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  cardOrderNum: {
    fontSize: 13,
    fontWeight: '700',
    color: '#1D4ED8',
  },
  cardGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 8,
    gap: 6,
  },
  cardGridItem: {
    flex: 1,
    minWidth: '45%',
    backgroundColor: '#F9FAFB',
    borderRadius: 6,
    padding: 6,
  },
  cardRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  cardLabel: {
    fontSize: 10,
    color: '#6A8A70',
    fontWeight: '600',
    width: 52,
    textTransform: 'uppercase',
  },
  cardValue: {
    fontSize: 11,
    color: '#1F2937',
    flex: 1,
  },
  cardActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 10,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
  },
  expandButton: {
    backgroundColor: '#EFF6FF',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#BFDBFE',
  },
  mapButton: {
    backgroundColor: '#F0FDF4',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#BBF7D0',
  },
  mapButtonText: {
    color: '#10B981',
    fontWeight: '600',
    fontSize: 11,
  },
  expandButtonText: {
    color: '#3B82F6',
    fontWeight: '600',
    fontSize: 11,
  },
  // Items sub-table
  itemsModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  itemsModalSheet: {
    backgroundColor: '#17201B',
    borderRadius: 16,
    width: '100%',
    maxWidth: 480,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowRadius: 20,
    elevation: 10,
  },
  itemsModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 18,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  itemsModalTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#1e293b',
  },
  itemsModalSub: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 2,
  },
  itemsModalClose: {
    padding: 4,
  },
  itemsModalTableHeader: {
    flexDirection: 'row',
    backgroundColor: '#141E18',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  itemsModalHeaderCell: {
    fontSize: 11,
    fontWeight: '700',
    color: '#94A3B8',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  itemsModalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  itemsModalImage: {
    width: 48,
    height: 48,
    borderRadius: 8,
    backgroundColor: '#F1F5F9',
  },
  itemsModalImagePlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 8,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  itemsModalProductName: {
    fontSize: 13,
    fontWeight: '600',
    color: '#1e293b',
  },
  itemsModalUnit: {
    fontSize: 11,
    color: '#94A3B8',
    marginTop: 2,
  },
  itemsModalCell: {
    fontSize: 13,
    color: '#8BAAB8',
  },
  itemsModalFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 14,
    backgroundColor: '#141E18',
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
  },
  itemsModalFooterLabel: {
    fontSize: 13,
    fontWeight: '700',
    color: '#64748B',
  },
  itemsModalFooterValue: {
    fontSize: 16,
    fontWeight: '800',
    color: '#10B981',
  },
  itemsSubTable: {
    backgroundColor: '#141E18',
    paddingHorizontal: 6,
    paddingVertical: 6,
    marginTop: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.09)',
  },
  itemsSubHeader: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderColor: 'rgba(255,255,255,0.09)',
    paddingBottom: 4,
    marginBottom: 2,
  },
  itemsSubRow: {
    flexDirection: 'row',
    paddingVertical: 4,
    borderRadius: 4,
  },
  itemsCell: {
    fontSize: 10,
    color: '#8BAAB8',
    textAlign: 'center',
    paddingHorizontal: 4,
  },
  itemName: {
    flex: 3,
    textAlign: 'left',
    fontWeight: '500',
  },
  itemQty: {
    flex: 1,
    fontWeight: '600',
    color: '#6B7280',
  },
  itemPrice: {
    flex: 1,
    fontWeight: '600',
    color: '#10B981',
  },
  // Empty state
  emptyBox: {
    alignItems: 'center',
    marginTop: 60,
  },
  emptyIcon: {
    fontSize: 36,
    marginBottom: 10,
  },
  emptyText: {
    fontSize: 13,
    color: '#6A8A70',
    fontWeight: '500',
  },
  // Three-dot menu
  threeDotButton: {
    padding: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  threeDotText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#6B7280',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.3)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuContainer: {
    backgroundColor: '#17201B',
    borderRadius: 12,
    paddingTop: 12,
    paddingBottom: 4,
    width: 200,
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
  },
  menuTitle: {
    fontSize: 11,
    fontWeight: '700',
    color: '#6A8A70',
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    paddingBottom: 10,
    paddingHorizontal: 16,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    gap: 10,
  },
  menuDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  menuText: {
    fontWeight: '700',
    fontSize: 13,
  },
});

// ── Statement drawer styles ───────────────────────────────────────
const sd = StyleSheet.create({
  root: { flex: 1, flexDirection: 'row', justifyContent: 'flex-end' },
  backdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(15,23,42,0.45)' },
  drawer: { height: '100%', backgroundColor: '#0E1512', shadowColor: '#000', shadowOffset: { width: -4, height: 0 }, shadowOpacity: 0.18, shadowRadius: 20, elevation: 16 },
  header: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#17201B', paddingHorizontal: 16, paddingTop: 16, paddingBottom: 14, borderBottomWidth: 1, borderBottomColor: '#e2e8f0', gap: 12 },
  closeBtn: { width: 32, height: 32, borderRadius: 16, backgroundColor: '#f1f5f9', justifyContent: 'center', alignItems: 'center' },
  closeTxt: { fontSize: 14, color: '#7A9A80', fontWeight: '600' },
  title: { flex: 1, fontSize: 17, fontWeight: '700', color: '#E2EDE6', textAlign: 'center' },
  label: { fontSize: 11, fontWeight: '800', color: '#7A9A80', letterSpacing: 0.5, textTransform: 'uppercase', marginTop: 16, marginBottom: 7 },
  segRow: { flexDirection: 'row', gap: 8 },
  seg: { flex: 1, paddingVertical: 10, borderRadius: 9, borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', backgroundColor: '#17201B', alignItems: 'center' },
  segOn: { backgroundColor: '#21808D', borderColor: '#21808D' },
  segTxt: { fontSize: 12, fontWeight: '700', color: '#7A9A80' },
  segTxtOn: { color: '#fff' },
  row2: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  textInput: { borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, fontSize: 13, color: '#E2EDE6', backgroundColor: '#17201B' },
  colGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  colChip: { paddingHorizontal: 11, paddingVertical: 7, borderRadius: 16, borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', backgroundColor: '#17201B' },
  colChipOn: { backgroundColor: '#e8f5f7', borderColor: '#21808D' },
  colChipTxt: { fontSize: 12, fontWeight: '600', color: '#7A9A80' },
  colChipTxtOn: { color: '#21808D' },
  note: { fontSize: 11, color: '#7A9A80', marginTop: 16, lineHeight: 16 },
  footer: { padding: 16, borderTopWidth: 1, borderTopColor: '#e2e8f0', backgroundColor: '#17201B' },
  downloadBtn: { backgroundColor: '#21808D', borderRadius: 10, paddingVertical: 14, alignItems: 'center' },
  downloadTxt: { color: '#fff', fontWeight: '700', fontSize: 15 },
});
